// @ts-nocheck
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useAppState, useDispatch } from '../store';
import * as api from '../api';
import ImportExcel from './ImportExcel';
import AdminPanel from './AdminPanel';

export default function Header() {
  const state = useAppState();
  const dispatch = useDispatch();
  const [showImport, setShowImport] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const [showGlobalSearch, setShowGlobalSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searching, setSearching] = useState(false);
  const searchTimer = useRef<any>(null);

  const handleLogout = async () => {
    await api.logout();
    dispatch({ type: 'SET_USER', user: null });
  };

  useEffect(() => {
    if (!searchQuery || searchQuery.length < 2) { setSearchResults([]); return; }
    setSearching(true);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(async () => {
      try { const { results } = await api.globalSearch(searchQuery); setSearchResults(results); }
      catch { setSearchResults([]); }
      setSearching(false);
    }, 300);
    return () => clearTimeout(searchTimer.current);
  }, [searchQuery]);

  const closeSearch = () => { setShowGlobalSearch(false); setSearchQuery(''); setSearchResults([]); };

  const handleSelectResult = async (boardId: string) => {
    closeSearch();
    dispatch({ type: 'SET_ACTIVE_BOARD', id: boardId });
    dispatch({ type: 'SET_LOADING', loading: true });
    try {
      const data = await api.getBoardData(boardId);
      dispatch({ type: 'SET_BOARD_DATA', groups: data.groups, columns: data.columns, items: data.items, cells: data.cells });
      dispatch({ type: 'SET_BOARD_ROLE', role: (data as any).boardRole || null });
    } catch (e) { console.error('Failed to load board:', e); }
    dispatch({ type: 'SET_LOADING', loading: false });
  };

  const handleExport = useCallback(async () => {
    if (!state.activeBoardId) return;
    const XLSX = await import('xlsx');
    const data = await api.exportBoardData(state.activeBoardId);
    const columns = [...data.columns].sort((a, b) => a.position - b.position);
    const groups = [...data.groups].sort((a, b) => a.position - b.position);
    const wb = XLSX.utils.book_new();
    const headerRow = ['Item', ...columns.map((c) => c.name)];
    const allRows: any[][] = [];
    for (const group of groups) {
      allRows.push([group.name]); allRows.push(headerRow);
      const groupItems = data.items.filter((i) => i.group_id === group.id).sort((a, b) => a.position - b.position);
      for (const item of groupItems) {
        const row: any[] = [item.name];
        for (const col of columns) {
          const cell = data.cells.find((cv) => cv.item_id === item.id && cv.column_id === col.id);
          if (cell) { try { const val = JSON.parse(cell.value); if (Array.isArray(val)) row.push(val.join(', ')); else if (typeof val === 'object' && val !== null) { if (val.start && val.end) row.push(`${val.start} to ${val.end}`); else row.push(JSON.stringify(val)); } else row.push(val); } catch { row.push(cell.value); } } else row.push('');
        }
        allRows.push(row);
      }
      allRows.push([]);
    }
    const ws = XLSX.utils.aoa_to_sheet(allRows);
    ws['!cols'] = headerRow.map((_, i) => ({ wch: Math.max(15, headerRow[i]?.length || 10) }));
    XLSX.utils.book_append_sheet(wb, ws, data.board.name.substring(0, 31));
    XLSX.writeFile(wb, `${data.board.name}.xlsx`);
  }, [state.activeBoardId]);

  const initials = state.user?.name?.split(' ').map((w) => w[0]).join('').substring(0, 2).toUpperCase() || '?';
  const isAdmin = state.user?.org_role === 'admin';

  const groupedResults = searchResults.reduce<Record<string, { board_name: string; board_id: string; items: any[] }>>((acc, r) => {
    if (!acc[r.board_id]) acc[r.board_id] = { board_name: r.board_name, board_id: r.board_id, items: [] };
    acc[r.board_id].items.push(r);
    return acc;
  }, {});

  return (
    <>
      <header className="app-header">
        <div className="header-left">
          <button className="sidebar-toggle" onClick={() => dispatch({ type: 'TOGGLE_SIDEBAR' })} style={{ fontSize: 20, color: '#676879' }}>
            &#9776;
          </button>
        </div>

        <div style={{ flex: 1 }} />

        <div className="header-right">
          {state.activeBoardId && (
            <>
              <button className="header-action-btn" onClick={() => setShowImport(true)}>Import</button>
              <button className="header-action-btn" onClick={handleExport}>Export</button>
            </>
          )}

          <button className="global-search-btn" onClick={() => setShowGlobalSearch(true)} title="Search everything (Ctrl+K)">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
          </button>

          {isAdmin && (
            <button className="header-action-btn admin" onClick={() => setShowAdmin(true)}>Admin</button>
          )}

          <div style={{ position: 'relative' }}>
            <button className="user-menu-btn" onClick={() => setShowUserMenu(!showUserMenu)}>
              {state.user?.avatar_url ? (
                <img src={state.user.avatar_url} alt="" className="user-avatar" style={{ width: 32, height: 32, borderRadius: '50%' }} />
              ) : (
                <div className="user-avatar">{initials}</div>
              )}
              <span style={{ fontSize: 13 }}>{state.user?.name}</span>
            </button>
            {showUserMenu && (
              <div style={{ position: 'absolute', top: '100%', right: 0, zIndex: 9998, background: 'white', borderRadius: 8, padding: 6, boxShadow: '0 8px 24px rgba(0,0,0,0.15)', minWidth: 200 }}>
                <div style={{ padding: '10px 14px' }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{state.user?.name}</div>
                  <div style={{ color: '#676879', fontSize: 12 }}>{state.user?.email}</div>
                </div>
                <div className="context-menu-divider" />
                <button className="context-menu-item" onClick={handleLogout}>Sign out</button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Global search full-page overlay */}
      {showGlobalSearch && createPortal(
        <div className="global-search-overlay" onClick={(e) => { if (e.target === e.currentTarget) closeSearch(); }}>
          <div className="global-search-modal">
            <div className="global-search-header">
              <input
                autoFocus className="global-search-input"
                value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search Everything ..."
                onKeyDown={(e) => e.key === 'Escape' && closeSearch()}
              />
            </div>
            <div className="global-search-body">
              {searchQuery.length < 2 ? (
                <div style={{ color: '#676879', fontSize: 14, padding: 20, textAlign: 'center' }}>
                  Type at least 2 characters to search across all boards
                </div>
              ) : searching ? (
                <div style={{ color: '#676879', fontSize: 14, padding: 20, textAlign: 'center' }}>Searching...</div>
              ) : searchResults.length === 0 ? (
                <div style={{ color: '#676879', fontSize: 14, padding: 20, textAlign: 'center' }}>No results found</div>
              ) : (
                Object.values(groupedResults).map((group) => (
                  <div key={group.board_id} style={{ marginBottom: 16 }}>
                    <div className="search-result-board">{group.board_name}</div>
                    {group.items.slice(0, 8).map((r: any) => (
                      <button key={r.item_id} className="search-result-item" onClick={() => handleSelectResult(r.board_id)}>
                        {r.item_name}
                      </button>
                    ))}
                    {group.items.length > 8 && (
                      <div style={{ padding: '4px 12px', fontSize: 12, color: '#676879' }}>+{group.items.length - 8} more results</div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {showImport && <ImportExcel onClose={() => setShowImport(false)} />}
      {showAdmin && <AdminPanel onClose={() => setShowAdmin(false)} />}
      {showUserMenu && <div style={{ position: 'fixed', inset: 0, zIndex: 9997 }} onClick={() => setShowUserMenu(false)} />}
    </>
  );
}
